import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ExternalLink, X, Upload, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const SERVICE_TYPES = [
  'Oil Change','Tire Rotation','Tire Replacement','Brake Service',
  'DOT Annual Inspection','PM Service','Engine Repair','Transmission',
  'Electrical','AC/Heat','Lights','Other'
];

const dueDateClass = (dateStr) => {
  if (!dateStr) return 'text-muted-foreground';
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (days <= 0) return 'text-red-400 font-semibold';
  if (days <= 14) return 'text-yellow-400 font-semibold';
  return 'text-green-400';
};

const dueMilesClass = (nextMiles, currentOdometer) => {
  if (!nextMiles) return 'text-muted-foreground';
  const milesLeft = nextMiles - (currentOdometer || 0);
  if (milesLeft <= 0) return 'text-red-400 font-semibold';
  if (milesLeft <= 500) return 'text-red-400 font-semibold';
  if (milesLeft <= 1500) return 'text-yellow-400 font-semibold';
  return 'text-green-400';
};

const emptyRecord = () => ({
  asset_id: '', unit_number: '', service_type: 'Oil Change',
  service_date: '', odometer_at_service: '', next_service_date: '',
  next_service_miles: '', vendor_name: '', vendor_phone: '',
  cost: 0, invoice_url: '', notes: '',
});

export default function ServiceRecordsTab({ alerts = [] }) {
  const [records, setRecords] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAsset, setFilterAsset] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form, setForm] = useState(emptyRecord());
  const [saving, setSaving] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const [recs, assetList] = await Promise.all([
      client.entities.MaintenanceRecord.list('-service_date', 500),
      client.entities.Asset.list('unit_number', 200),
    ]);
    setRecords(recs);
    setAssets(assetList);
    setLoading(false);
  };

  const openAdd = () => {
    setEditRecord(null);
    setForm(emptyRecord());
    setInvoiceFile(null);
    setAutoFilledFields(new Set());
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditRecord(r);
    setForm({ ...emptyRecord(), ...r });
    setInvoiceFile(null);
    setAutoFilledFields(new Set());
    setShowModal(true);
  };

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInvoiceLoading(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const isImage = file.type.startsWith('image/');
      const response = await client.functions.invoke('parseMaintenanceInvoice', {
        pdfBase64: base64,
        mediaType: isImage ? file.type : 'application/pdf',
        isImage,
      });
      const result = response.data;
      if (result && Object.keys(result).length > 0) {
        const filledKeys = Object.keys(result).filter(k => result[k] !== '' && result[k] !== 0 && result[k] != null);
        setForm(prev => {
          const updated = { ...prev, ...result };
          // Match unit_number to asset
          if (result.unit_number) {
            const matched = assets.find(a => a.unit_number?.toLowerCase() === result.unit_number?.toLowerCase());
            if (matched) { updated.asset_id = matched.id; updated.unit_number = matched.unit_number; }
          }
          return updated;
        });
        setAutoFilledFields(new Set(filledKeys));
        setTimeout(() => setAutoFilledFields(new Set()), 3000);
        // Auto-upload the file as invoice attachment
        const uploaded = await client.integrations.Core.UploadFile({ file });
        if (uploaded?.file_url) setForm(prev => ({ ...prev, invoice_url: uploaded.file_url }));
        setInvoiceFile(file);
        toast.success('Invoice parsed — please review and confirm the fields.');
      } else {
        toast.error('Could not extract data. Please fill in manually.');
      }
    } catch {
      toast.error('Failed to parse invoice.');
    } finally {
      setInvoiceLoading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.asset_id) { toast.error('Asset is required.'); return; }
    if (!form.service_date) { toast.error('Service date is required.'); return; }
    setSaving(true);
    let invoice_url = form.invoice_url;
    if (invoiceFile) {
      setUploadingInvoice(true);
      const { file_url } = await client.integrations.Core.UploadFile({ file: invoiceFile });
      invoice_url = file_url;
      setUploadingInvoice(false);
    }
    const payload = { ...form, invoice_url, cost: parseFloat(form.cost) || 0 };
    if (editRecord) {
      await client.entities.MaintenanceRecord.update(editRecord.id, payload);
      toast.success('Service record updated.');
    } else {
      await client.entities.MaintenanceRecord.create(payload);
      toast.success('Service record added.');
    }
    // Update asset odometer if service odometer is higher
    if (form.asset_id && form.odometer_at_service) {
      const asset = assets.find(a => a.id === form.asset_id);
      if (asset && parseFloat(form.odometer_at_service) > (asset.current_odometer || 0)) {
        await client.entities.Asset.update(form.asset_id, { current_odometer: parseFloat(form.odometer_at_service) });
      }
    }
    setSaving(false);
    setShowModal(false);
    loadAll();
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete service record for Unit ${r.unit_number}?`)) return;
    await client.entities.MaintenanceRecord.delete(r.id);
    toast.success('Deleted.');
    loadAll();
  };

  const handleAssetChange = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    setForm(p => ({ ...p, asset_id: assetId, unit_number: asset?.unit_number || '' }));
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const hl = (key) => autoFilledFields.has(key) ? 'border-primary/60 ring-1 ring-primary/40' : 'border-border';

  const filtered = records.filter(r => {
    if (filterAsset && r.asset_id !== filterAsset) return false;
    if (filterType && r.service_type !== filterType) return false;
    if (filterDateFrom && r.service_date < filterDateFrom) return false;
    if (filterDateTo && r.service_date > filterDateTo) return false;
    if (search) {
      const q = search.toLowerCase();
      return (r.unit_number || '').toLowerCase().includes(q) ||
        (r.vendor_name || '').toLowerCase().includes(q) ||
        (r.service_type || '').toLowerCase().includes(q);
    }
    return true;
  });

  const getAssetOdometer = (assetId) => assets.find(a => a.id === assetId)?.current_odometer || 0;

  return (
    <div className="space-y-4">
      {alerts.length > 0 && <ServiceAlertBanner alerts={alerts} />}

      {/* Controls */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Asset</label>
            <select value={filterAsset} onChange={e => setFilterAsset(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              <option value="" className="bg-background">All Units</option>
              {assets.map(a => <option key={a.id} value={a.id} className="bg-background">{a.unit_number} ({a.asset_type})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Service Type</label>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              <option value="" className="bg-background">All Types</option>
              {SERVICE_TYPES.map(t => <option key={t} value={t} className="bg-background">{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">From</label>
            <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="text-foreground text-xs h-8 w-36" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">To</label>
            <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="text-foreground text-xs h-8 w-36" />
          </div>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            className="text-foreground h-8 text-xs w-40" />
        </div>
        <Button onClick={openAdd} size="sm" className="bg-primary text-primary-foreground h-8 text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Service Record
        </Button>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Unit #','Type','Service Type','Date','Odometer','Next Due Date','Next Due Miles','Vendor','Cost',''].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(r => {
                const odo = getAssetOdometer(r.asset_id);
                return (
                  <tr key={r.id} className="hover:bg-white/3 transition-colors">
                    <td className="px-3 py-2.5 font-semibold text-foreground">{r.unit_number || '—'}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{assets.find(a => a.id === r.asset_id)?.asset_type || '—'}</td>
                    <td className="px-3 py-2.5 text-foreground">{r.service_type}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{r.service_date}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{r.odometer_at_service?.toLocaleString() || '—'}</td>
                    <td className={cn("px-3 py-2.5", dueDateClass(r.next_service_date))}>{r.next_service_date || '—'}</td>
                    <td className={cn("px-3 py-2.5", dueMilesClass(r.next_service_miles, odo))}>
                      {r.next_service_miles ? r.next_service_miles.toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{r.vendor_name || '—'}</td>
                    <td className="px-3 py-2.5 font-semibold text-foreground">${(r.cost || 0).toLocaleString()}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button onClick={() => openEdit(r)} className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        {r.invoice_url && (
                          <a href={r.invoice_url} target="_blank" rel="noopener noreferrer" className="p-1 rounded text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 transition-colors" title="View Invoice">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button onClick={() => handleDelete(r)} className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">{loading ? 'Loading...' : 'No service records found.'}</p>}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="flex min-h-full items-start justify-center p-4">
            <div className="relative w-full max-w-2xl my-8 p-6 space-y-4 z-10 rounded-2xl border border-border bg-background">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-space font-semibold text-foreground text-lg">{editRecord ? 'Edit Service Record' : 'Add Service Record'}</h2>
                <div className="flex items-center gap-2">
                  <label className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors",
                    invoiceLoading
                      ? "bg-primary/10 border-primary/20 text-primary/50 cursor-not-allowed pointer-events-none"
                      : "bg-primary/20 border-primary/40 text-primary hover:bg-primary/30"
                  )}>
                    {invoiceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {invoiceLoading ? 'Reading Invoice...' : 'Auto Fill From Invoice'}
                    <input type="file" accept=".pdf,image/*" className="hidden" onChange={handleInvoiceUpload} disabled={invoiceLoading} />
                  </label>
                  <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-xs text-muted-foreground block mb-1">Asset *</label>
                  <select value={form.asset_id} onChange={e => handleAssetChange(e.target.value)}
                    className={cn("w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm", hl('asset_id'))}>
                    <option value="" className="bg-background">Select asset...</option>
                    {assets.map(a => <option key={a.id} value={a.id} className="bg-background">{a.unit_number} — {a.asset_type}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Service Type *</label>
                  <select value={form.service_type} onChange={e => set('service_type', e.target.value)}
                    className={cn("w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm", hl('service_type'))}>
                    {SERVICE_TYPES.map(t => <option key={t} value={t} className="bg-background">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Service Date *</label>
                  <Input type="date" value={form.service_date} onChange={e => set('service_date', e.target.value)} className={cn("text-foreground text-sm", hl('service_date'))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Odometer at Service</label>
                  <Input type="number" value={form.odometer_at_service || ''} onChange={e => set('odometer_at_service', parseFloat(e.target.value) || '')} className={cn("text-foreground text-sm", hl('odometer_at_service'))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Next Service Date</label>
                  <Input type="date" value={form.next_service_date || ''} onChange={e => set('next_service_date', e.target.value)} className={cn("text-foreground text-sm", hl('next_service_date'))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Next Service Miles</label>
                  <Input type="number" value={form.next_service_miles || ''} onChange={e => set('next_service_miles', parseFloat(e.target.value) || '')} className={cn("text-foreground text-sm", hl('next_service_miles'))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Vendor / Shop</label>
                  <Input value={form.vendor_name || ''} onChange={e => set('vendor_name', e.target.value)} className={cn("text-foreground text-sm", hl('vendor_name'))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Vendor Phone</label>
                  <Input value={form.vendor_phone || ''} onChange={e => set('vendor_phone', e.target.value)} className={cn("text-foreground text-sm", hl('vendor_phone'))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Cost ($)</label>
                  <Input type="number" value={form.cost || 0} onChange={e => set('cost', parseFloat(e.target.value) || 0)} className={cn("text-foreground text-sm", hl('cost'))} />
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <label className="text-xs text-muted-foreground block mb-1">Invoice / Receipt</label>
                  <label className={cn("flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors",
                    invoiceFile ? "border-primary/60 bg-primary/5" : "border-border hover:border-primary/40")}>
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setInvoiceFile(e.target.files[0])} />
                    {invoiceFile ? <FileText className="w-4 h-4 text-primary" /> : <Upload className="w-4 h-4 text-muted-foreground" />}
                    <span className={cn("text-sm", invoiceFile ? "text-primary" : "text-muted-foreground")}>
                      {invoiceFile ? invoiceFile.name : form.invoice_url ? 'Replace invoice...' : 'Upload invoice (PDF/JPG/PNG)'}
                    </span>
                  </label>
                  {form.invoice_url && !invoiceFile && (
                    <a href={form.invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 mt-1 hover:underline">
                      <ExternalLink className="w-3 h-3" /> View existing invoice
                    </a>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <label className="text-xs text-muted-foreground block mb-1">Notes</label>
                  <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} className={cn("w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm resize-none", hl('notes'))} />
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
                  {uploadingInvoice ? 'Uploading...' : saving ? 'Saving...' : 'Save Record'}
                </Button>
                <Button onClick={() => setShowModal(false)} variant="outline" className="border-border">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceAlertBanner({ alerts }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || alerts.length === 0) return null;
  const critical = alerts.filter(a => a.severity === 'critical');
  const high = alerts.filter(a => a.severity === 'high');
  const medium = alerts.filter(a => a.severity === 'medium');
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      {critical.length > 0 && (
        <div className="bg-red-500/10 border-b border-border px-4 py-3">
          <p className="text-xs font-bold text-red-400 mb-2">🔴 CRITICAL — Overdue</p>
          {critical.map((a, i) => <p key={i} className="text-xs text-red-300">{a.label}{a.date ? ` · ${a.date}` : ''}</p>)}
        </div>
      )}
      {high.length > 0 && (
        <div className="bg-amber-500/10 border-b border-border px-4 py-3">
          <p className="text-xs font-bold text-amber-400 mb-2">🟡 HIGH — Due within 7 days / 500 miles</p>
          {high.map((a, i) => <p key={i} className="text-xs text-amber-300">{a.label}{a.date ? ` · ${a.date}` : ''}</p>)}
        </div>
      )}
      {medium.length > 0 && (
        <div className="bg-blue-500/10 px-4 py-3">
          <p className="text-xs font-bold text-blue-400 mb-2">🔵 MEDIUM — Due within 14–30 days / 1,500 miles</p>
          {medium.map((a, i) => <p key={i} className="text-xs text-blue-300">{a.label}{a.date ? ` · ${a.date}` : ''}</p>)}
        </div>
      )}
      <div className="bg-white/3 px-4 py-2 flex justify-end">
        <button onClick={() => setDismissed(true)} className="text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
      </div>
    </div>
  );
}